import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2, Video, BadgeDollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BUSINESS } from "@/lib/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EQUIPMENT_TYPES = [
  "HVAC",
  "Refrigeration",
  "Cooking Equipment",
  "Other",
];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  equipment_type: "",
  issue_description: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      e.phone = "Valid phone is required";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Valid email is required";
    if (!form.equipment_type) e.equipment_type = "Please select equipment";
    if (!form.issue_description.trim() || form.issue_description.trim().length < 5)
      e.issue_description = "Tell us a little about the issue (5+ chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Request submitted!");
      setDone(true);
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err.message || "Submission failed";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-slate-50">
      {/* HEADER */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-xs tracking-[0.22em] uppercase font-semibold text-cyan-400">
            Contact
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl">
            Tell us what's broken. We'll get to work.
          </h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">
            Fill out the form below or call us directly. We respond to every request
            personally — usually within the same business day.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="contact-pricing-badges">
            <span className="inline-flex items-center gap-2 border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 px-4 py-2 text-xs tracking-[0.18em] uppercase font-bold">
              <Video className="w-3.5 h-3.5" /> Free phone &amp; video consult
            </span>
            <span className="inline-flex items-center gap-2 border border-white/20 bg-white/5 text-white px-4 py-2 text-xs tracking-[0.18em] uppercase font-bold">
              <BadgeDollarSign className="w-3.5 h-3.5" /> $65 service call
            </span>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* INFO */}
          <aside className="md:col-span-4 space-y-8">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-blue-900">
                Direct line
              </div>
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-phone-link"
                className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-2 inline-flex items-center gap-3 hover:text-blue-900 transition-colors"
              >
                <Phone className="w-6 h-6 text-blue-900" />
                {BUSINESS.phone}
              </a>
              <div className="text-sm text-slate-500 mt-2">
                Down equipment? Calling is fastest.
              </div>
            </div>

            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-blue-900">
                Email
              </div>
              <a
                href={BUSINESS.emailHref}
                data-testid="contact-email-link"
                className="text-slate-900 mt-2 inline-flex items-center gap-2 font-semibold break-all hover:text-blue-900 transition-colors"
              >
                <Mail className="w-4 h-4 text-blue-900" />
                {BUSINESS.email}
              </a>
            </div>

            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-blue-900">
                Service area
              </div>
              <div className="text-slate-900 mt-2 inline-flex items-center gap-2 font-semibold">
                <MapPin className="w-4 h-4 text-blue-900" />
                {BUSINESS.serviceArea}
              </div>
              <div className="text-sm text-slate-500 mt-2">
                Medford · Ashland · Grants Pass · Klamath Falls and surrounding.
              </div>
            </div>

            <div className="border border-slate-200 bg-white p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-blue-900">
                Hours
              </div>
              <div className="mt-3 text-sm text-slate-700 space-y-1">
                <div className="flex justify-between"><span>Mon – Fri</span><span>7a – 6p</span></div>
                <div className="flex justify-between"><span>Saturday</span><span>By appointment</span></div>
                <div className="flex justify-between"><span>Sunday</span><span>Emergency only</span></div>
              </div>
            </div>
          </aside>

          {/* FORM */}
          <div className="md:col-span-8">
            <div className="bg-white border border-slate-200 p-6 sm:p-10">
              {done ? (
                <div data-testid="contact-success" className="text-center py-10">
                  <div className="mx-auto w-14 h-14 bg-blue-900 text-white grid place-items-center">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h2 className="font-display font-black text-3xl text-slate-900 mt-6">
                    Request received.
                  </h2>
                  <p className="text-slate-600 mt-3 max-w-md mx-auto">
                    Thanks — we got it. {BUSINESS.name} will reach out shortly to confirm
                    timing. For urgent issues, please call {BUSINESS.phone}.
                  </p>
                  <button
                    data-testid="contact-submit-another"
                    onClick={() => setDone(false)}
                    className="mt-8 inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 px-6 py-3 text-sm font-semibold tracking-wide uppercase transition-colors"
                  >
                    Submit another
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-6">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-blue-900">
                      Repair request
                    </div>
                    <h2 className="font-display font-black text-2xl sm:text-3xl text-slate-900 mt-2">
                      Tell us about the equipment.
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                        Full name
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Jane Smith"
                        className="mt-2 rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:border-blue-900"
                      />
                      {errors.name && (
                        <div className="text-xs text-red-600 mt-1">{errors.name}</div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="(541) 555-0144"
                        className="mt-2 rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:border-blue-900"
                      />
                      {errors.phone && (
                        <div className="text-xs text-red-600 mt-1">{errors.phone}</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@business.com"
                        className="mt-2 rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:border-blue-900"
                      />
                      {errors.email && (
                        <div className="text-xs text-red-600 mt-1">{errors.email}</div>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                        Equipment type
                      </Label>
                      <Select
                        value={form.equipment_type}
                        onValueChange={(v) => update("equipment_type", v)}
                      >
                        <SelectTrigger
                          data-testid="contact-select-equipment"
                          className="mt-2 rounded-none border-slate-300 focus:ring-blue-900 focus:border-blue-900 h-10"
                        >
                          <SelectValue placeholder="Select equipment" />
                        </SelectTrigger>
                        <SelectContent>
                          {EQUIPMENT_TYPES.map((t) => (
                            <SelectItem
                              key={t}
                              value={t}
                              data-testid={`equipment-option-${t.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.equipment_type && (
                        <div className="text-xs text-red-600 mt-1">{errors.equipment_type}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="issue" className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                      Describe the issue
                    </Label>
                    <Textarea
                      id="issue"
                      data-testid="contact-input-issue"
                      rows={6}
                      value={form.issue_description}
                      onChange={(e) => update("issue_description", e.target.value)}
                      placeholder="Make / model, what's happening, when it started, anything else helpful…"
                      className="mt-2 rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:border-blue-900"
                    />
                    {errors.issue_description && (
                      <div className="text-xs text-red-600 mt-1">{errors.issue_description}</div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pt-2">
                    <div className="text-xs text-slate-500">
                      We'll only use your info to respond to this request.
                    </div>
                    <button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={submitting}
                      className="inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-blue-800 disabled:opacity-60 text-white px-7 py-3.5 text-sm font-semibold tracking-wide uppercase transition-colors"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          Send request <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
